import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Trash2, ShoppingBag, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty!');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="bg-off_white min-h-screen flex items-center justify-center py-12" data-testid="empty-cart">
        <div className="text-center">
          <div className="bg-brand_yellow w-32 h-32 rounded-full border-4 border-black mx-auto mb-6 flex items-center justify-center">
            <ShoppingBag className="w-16 h-16" strokeWidth={3} />
          </div>
          <h2 className="font-chewy text-4xl text-brand_blue mb-4">Your Cart is Empty</h2>
          <p className="font-nunito text-xl text-gray-700 mb-8 font-semibold">
            Time to fill it up with some delicious lemonade!
          </p>
          <button
            onClick={() => navigate('/products')}
            className="bg-brand_blue text-white font-chewy text-xl px-8 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all"
            data-testid="shop-now-button"
          >
            START SHOPPING
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-chewy text-5xl sm:text-7xl text-center mb-12">
          <span className="text-brand_blue">YOUR</span>{' '}
          <span className="text-brand_red">CART</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.product_id}
                className="bg-white border-4 border-black rounded-xl p-4 shadow-hard"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="flex gap-4">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg border-2 border-black"
                  />
                  <div className="flex-grow">
                    <h3 className="font-chewy text-2xl text-brand_blue mb-2">
                      {item.name}
                    </h3>
                    <p className="font-nunito text-lg font-bold text-brand_red mb-4">
                      ${item.price.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="bg-white border-2 border-black rounded-full w-8 h-8 flex items-center justify-center shadow-hard hover:shadow-hard-hover transition-all"
                          data-testid={`decrease-quantity-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" strokeWidth={3} />
                        </button>
                        <span className="font-nunito font-bold text-lg w-8 text-center" data-testid={`quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="bg-white border-2 border-black rounded-full w-8 h-8 flex items-center justify-center shadow-hard hover:shadow-hard-hover transition-all"
                          data-testid={`increase-quantity-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" strokeWidth={3} />
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          removeFromCart(item.product_id);
                          toast.success('Item removed from cart');
                        }}
                        className="ml-auto bg-brand_red text-white px-4 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={3} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => {
                clearCart();
                toast.success('Cart cleared');
              }}
              className="w-full bg-white text-brand_red font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
              data-testid="clear-cart-button"
            >
              Clear Cart
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard sticky top-24">
              <h2 className="font-chewy text-3xl text-brand_blue mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between font-nunito font-semibold text-lg">
                  <span>Subtotal:</span>
                  <span data-testid="cart-subtotal">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="border-t-2 border-black pt-4">
                  <div className="flex justify-between font-chewy text-2xl text-brand_red">
                    <span>Total:</span>
                    <span data-testid="cart-total">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full bg-brand_blue text-white font-chewy text-xl px-6 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all"
                data-testid="checkout-button"
              >
                PROCEED TO CHECKOUT
              </button>
              <button
                onClick={() => navigate('/products')}
                className="w-full mt-4 bg-white text-black font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;